import { useEffect, useState } from 'react'
import type { User } from 'firebase/auth'
import { doc, onSnapshot, getFirestore } from 'firebase/firestore'
import { app } from '../../firebase'
import { translations, type Language } from '../../translations'
import { TRIAL_DAILY_LIMIT } from './constants'

interface TrialInfo {
    trialActive: boolean
    trialDaysRemaining: number
    dailyLimit: number
    dailyUsed: number
    plan: string
}

interface TrialBannerProps {
    user: User | null
    lang: Language
    onUpgradeClick: () => void
}

export function TrialBanner({ user, lang, onUpgradeClick }: TrialBannerProps) {
    const t = translations[lang]
    const isRTL = lang === 'ar'

    const [trialInfo, setTrialInfo] = useState<TrialInfo | null>(null)
    const [dismissed, setDismissed] = useState(false)

    useEffect(() => {
        if (!user) {
            setTrialInfo(null)
            return
        }

        const db = getFirestore(app, 'dxt-law-db')
        const userRef = doc(db, 'users', user.uid)

        // Use real-time listener to update usage count automatically
        const unsubscribe = onSnapshot(
            userRef,
            (snapshot) => {
                if (!snapshot.exists()) {
                    setTrialInfo(null)
                    return
                }

                const data = snapshot.data()
                const plan = data.plan || 'free'

                // Only show for 'trial' plan
                // The 'free' users will see the "Start Trial" button in the UsersList or modal
                // We might want a different banner for 'free' users later ("Start your trial now!")
                if (plan !== 'trial') {
                    setTrialInfo(null)
                    return
                }

                // Calculate remaining days from subscriptionEnd
                let trialActive = false
                let trialDaysRemaining = 0
                const subscriptionEnd = data.subscriptionEnd

                if (subscriptionEnd) {
                    const endDate = subscriptionEnd.toDate()
                    const now = new Date()
                    const msRemaining = endDate.getTime() - now.getTime()
                    const daysRemaining = Math.ceil(msRemaining / (1000 * 60 * 60 * 24))

                    trialActive = daysRemaining > 0
                    trialDaysRemaining = Math.max(0, daysRemaining)
                }

                setTrialInfo({
                    trialActive,
                    trialDaysRemaining,
                    dailyLimit: TRIAL_DAILY_LIMIT,
                    dailyUsed: data.daily_count || 0,
                    plan,
                })
            },
            (error) => {
                console.error('Error listening to trial info:', error)
            }
        )

        // Cleanup listener on unmount or user change
        return () => unsubscribe()
    }, [user])

    // Don't show banner if:
    // - No user
    // - No trial info
    // - Premium user
    // - Banner dismissed today
    if (!user || !trialInfo || trialInfo.plan === 'premium' || dismissed) {
        return null
    }

    // Check if dismissed today
    const dismissKey = `trial_banner_dismissed_${user.uid}`
    const dismissedToday = localStorage.getItem(dismissKey) === new Date().toDateString()
    if (dismissedToday && !trialInfo.trialActive) {
        return null
    }

    const handleDismiss = () => {
        setDismissed(true)
        localStorage.setItem(dismissKey, new Date().toDateString())
    }

    const isEndingSoon = trialInfo.trialActive && trialInfo.trialDaysRemaining <= 2
    const isLastDay = trialInfo.trialDaysRemaining === 1
    const isExpired = !trialInfo.trialActive

    // Banner message
    let bannerText = ''
    if (isExpired) {
        bannerText = t.trialBannerExpired
    } else if (isLastDay) {
        bannerText = t.trialBannerLastDay
    } else {
        bannerText = t.trialBanner.replace('{days}', String(trialInfo.trialDaysRemaining))
    }

    // Usage text
    const usageText = t.trialUsage
        .replace('{used}', String(trialInfo.dailyUsed))
        .replace('{limit}', String(trialInfo.dailyLimit))

    return (
        <div
            className={`relative border-b px-4 py-2 text-sm ${isExpired
                    ? 'border-red-200 bg-red-50 text-red-800'
                    : isEndingSoon
                        ? 'border-amber-200 bg-amber-50 text-amber-800'
                        : 'border-blue-200 bg-blue-50 text-blue-800'
                }`}
            dir={isRTL ? 'rtl' : 'ltr'}
        >
            <div className="mx-auto flex max-w-5xl items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                    {/* Icon */}
                    <div
                        className={`flex h-6 w-6 items-center justify-center rounded-full ${isExpired
                                ? 'bg-red-100'
                                : isEndingSoon
                                    ? 'bg-amber-100'
                                    : 'bg-blue-100'
                            }`}
                    >
                        {isExpired ? (
                            <svg
                                className="h-4 w-4 text-red-600"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                            </svg>
                        ) : (
                            <svg
                                className={`h-4 w-4 ${isEndingSoon ? 'text-amber-600' : 'text-blue-600'}`}
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                            </svg>
                        )}
                    </div>

                    {/* Text */}
                    <div className="flex flex-col sm:flex-row sm:items-center sm:gap-3">
                        <span className="font-medium">{bannerText}</span>
                        <span className="text-xs opacity-75 sm:text-sm">{usageText}</span>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    {/* Upgrade button */}
                    <button
                        type="button"
                        onClick={onUpgradeClick}
                        className={`whitespace-nowrap rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors sm:text-sm ${isExpired
                                ? 'bg-red-600 text-white hover:bg-red-700'
                                : isEndingSoon
                                    ? 'bg-amber-600 text-white hover:bg-amber-700'
                                    : 'bg-blue-600 text-white hover:bg-blue-700'
                            }`}
                    >
                        {t.trialUpgradeCta}
                    </button>

                    {/* Dismiss button (only for non-expired) */}
                    {!isExpired && (
                        <button
                            type="button"
                            onClick={handleDismiss}
                            className="p-1 opacity-50 transition-opacity hover:opacity-100"
                            aria-label="Dismiss"
                        >
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M6 18L18 6M6 6l12 12"
                                />
                            </svg>
                        </button>
                    )}
                </div>
            </div>
        </div>
    )
}
